/**
 * Cookie consent banner customizations (works with global-styles.css):
 *
 * 1. Decline button visibility — Decline is hidden by default
 *    (global-styles.css) and revealed once Shopify's native banner is
 *    confirmed present in the DOM, by adding the `consent-required-region`
 *    class to <html>. This is a pure DOM-presence check, NOT an API call —
 *    deliberately, so it has no dependency on Shopify.customerPrivacy ever
 *    loading. It's safe because banner-visibility and blocking-by-default
 *    are the SAME Admin setting (confirmed 2026-07-13): Shopify's native
 *    banner only renders in regions Admin has marked consent-required, so
 *    "banner present" already IMPLIES "Decline must show" with no need to
 *    separately query the region's allowed/blocked state. (Opt-in-by-default
 *    regions get no native banner at all — see notice-consent-banner.js,
 *    which handles those with a separate, non-blocking notice banner.)
 *
 * 2. Inline "Manage preferences" — shown for all visitors and relocated to sit
 *    right after the privacy policy link in the banner body, styled as a link.
 *
 * Final architecture (2026-07-13): Shopify's native banner + this file handle
 * consent-required (opt-out-by-default) regions end to end — banner render,
 * blocking, and persistence are entirely Shopify's platform responsibility,
 * untouched by any theme code. notice-consent-banner.js is the separate,
 * lightweight companion for opt-in-by-default regions, which Shopify's native
 * banner structurally cannot serve without also blocking them.
 */
(function () {
  'use strict';

  // Reveal Shopify's Decline button. Called only once the banner element
  // itself is confirmed present (see relocateManagePrefs below) — at that
  // point consent-required-region is unconditionally correct, so this is a
  // one-way flip with nothing left to re-check on later calls.
  function revealDecline() {
    document.documentElement.classList.add('consent-required-region');
  }

  /* ------------------------------------------------------------------ *
   * 2. Move "Manage preferences" to the end of the banner body copy,    *
   *    i.e. right after the "...Learn more in our Privacy Policy" text.  *
   *    Works whether the banner is in the light DOM or a shadow root.    *
   * ------------------------------------------------------------------ */

  var BANNER_SELECTOR = '#shopify-pc__banner, .shopify-pc__banner';
  var cachedBanner = null;
  var warnedNoBanner = false;
  var warnedNoBtn = false;

  // querySelector that also descends into (open) shadow roots.
  function deepQuery(selector, root) {
    var el = root.querySelector(selector);
    if (el) return el;
    var nodes = root.querySelectorAll('*');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].shadowRoot) {
        var found = deepQuery(selector, nodes[i].shadowRoot);
        if (found) return found;
      }
    }
    return null;
  }

  function getBanner() {
    if (cachedBanner && cachedBanner.isConnected) return cachedBanner;
    cachedBanner = deepQuery(BANNER_SELECTOR, document);
    return cachedBanner;
  }

  // Find the privacy policy link within the banner body. Prefer a link whose
  // href mentions "privacy", then any policy link, then the last link present.
  function findPolicyLink(banner) {
    var links = banner.querySelectorAll('a');
    var lastPolicy = null;
    for (var i = 0; i < links.length; i++) {
      var href = (links[i].getAttribute('href') || '').toLowerCase();
      if (href.indexOf('privacy') !== -1) return links[i];
      if (href.indexOf('/policies/') !== -1) lastPolicy = links[i];
    }
    return lastPolicy || (links.length ? links[links.length - 1] : null);
  }

  // Append the button to the END of the body copy (the paragraph that contains
  // the privacy policy link) so it follows the whole sentence. Idempotent: a
  // no-op once it's already the last element.
  function relocateManagePrefs() {
    var banner = getBanner();
    if (!banner) return;

    // Banner confirmed present -- Decline must show (see revealDecline above).
    revealDecline();

    var btn = banner.querySelector('.shopify-pc__banner__btn-manage-prefs');
    if (!btn) {
      if (!warnedNoBtn) {
        warnedNoBtn = true;
        console.warn('[consent] Banner found but no .shopify-pc__banner__btn-manage-prefs. Buttons present:',
          [].map.call(banner.querySelectorAll('button'), function (b) { return b.className; }));
      }
      return;
    }

    var link = findPolicyLink(banner);
    var body = (link && link.parentNode) ||
      banner.querySelector('.shopify-pc__banner__body') ||
      banner.querySelector('.shopify-pc__banner__wrapper') ||
      banner;

    if (body.lastElementChild === btn) return; // already in place

    body.appendChild(btn);
    btn.classList.add('manage-prefs-inline');

    // Reword to "To manage your preferences click here", with only "click here"
    // as the link. The label text precedes the button; an aria-label keeps the
    // action meaningful for screen readers (since "click here" alone is poor).
    btn.textContent = 'click here';
    btn.setAttribute('aria-label', 'Manage cookie preferences');
    body.insertBefore(document.createTextNode(' - To manage your preferences'), btn);

    // The theme stylesheet can't reach inside a shadow root, so style inline there.
    if (banner.getRootNode() !== document) {
      btn.style.cssText += ';display:inline;margin:0 0 0 .4em;padding:0;border:0;' +
        'background:none;font:inherit;color:inherit;text-decoration:underline;cursor:pointer;';
    }
  }

  // The banner is injected (and may be re-rendered) by Shopify asynchronously,
  // so watch the DOM and also poll as a safety net.
  relocateManagePrefs();

  var observer = null;
  if (typeof MutationObserver === 'function') {
    observer = new MutationObserver(relocateManagePrefs);
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  var tries = 0;
  var poll = setInterval(function () {
    relocateManagePrefs();
    if (++tries >= 40) { // ~20s
      clearInterval(poll);
      if (!cachedBanner) {
        if (observer) observer.disconnect();
        if (!warnedNoBanner) {
          warnedNoBanner = true;
          console.warn('[consent] No consent banner (' + BANNER_SELECTOR +
            ') found in light or shadow DOM after 20s; "Manage preferences" not relocated.');
        }
      }
    }
  }, 500);
})();
